import React from 'react';

import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { ACCOMMODATION_PROCESS_REFERENCE_CARD, PRIORITIZATION_ORDER_REFERENCE_CARD } from 'src/config/ExternalLinks';
import {
  setIsTldAlertModalOpen,
  setJmrFilter,
  setTldAlertModalAcknowledged,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { JmrJobTypes } from 'src/utils/jmrUtils';

interface TldAlertModalProps {
  onConfirm: () => void;
  isOpen: boolean;
}

const TldAlertModal = ({ onConfirm, isOpen }: TldAlertModalProps) => {
  const { jmrFilter, jmrResults } = useAppSelector((state) => state.newJmrPage);
  const dispatch = useAppDispatch();
  const closeModal = () =>
    dispatch(
      setIsTldAlertModalOpen({
        open: false,
      }),
    );

  const eligibleProcessPathJobs = jmrResults.filter(
    (r) => r.jobType === JmrJobTypes.ProcessPath && (r.jobCapacity != null ? r.jobCapacity > 0 : true),
  );

  return (
    <Modal
      title="Are you sure TLD is the best match?"
      open={isOpen}
      scrollContainer="viewport"
      closeLabel="Close"
      aria-describedby="tld-modal-description"
      width="767px"
      data-cy="new-jmr__tld-modal"
      onClose={closeModal}
    >
      <Box id="tld-modal-description">
        <Column spacing="450">
          <Alert type="warning">
            TLD selected when a Process Path is available. This override will be tracked as A TLD defect in the RTW
            dashboard.
          </Alert>
          <Text type="b300">
            Consider selecting one or more of the process path(s) that has been matched to the associate's restrictions
            or adjust a role with an approved modification (e.g., stool or andon).
          </Text>
          <Box>
            <Text type="b300">
              <b>Eligible Process Paths:</b>
            </Text>
            {eligibleProcessPathJobs.slice(0, 3).map((row) => (
              <Text key={row.id} type="b300">
                {row.name}
              </Text>
            ))}
            {eligibleProcessPathJobs.length - 3 > 0 && (
              <Text type="b300">+ {eligibleProcessPathJobs.length - 3} more</Text>
            )}
          </Box>
          <Box>
            <Text type="b300">For more information visit:</Text>
            <Text type="b300">
              <Link type="secondary" href={PRIORITIZATION_ORDER_REFERENCE_CARD} target="_blank" rel="noopener">
                TWP Prioritization Order
              </Link>
            </Text>
            <Text type="b300">
              <Link type="secondary" href={ACCOMMODATION_PROCESS_REFERENCE_CARD} target="_blank" rel="noopener">
                Accommodations Process Network Guidance Overview
              </Link>
            </Text>
          </Box>
        </Column>
      </Box>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button
            type="secondary"
            size="small"
            data-cy="tld-alert-modal__keep_selection"
            onClick={() => {
              dispatch(setTldAlertModalAcknowledged());
              onConfirm();
              closeModal();
            }}
          >
            Continue with TLD Override
          </Button>
          <Button
            type="primary"
            size="small"
            data-cy="tld-alert-modal__go-back"
            onClick={() => {
              dispatch(
                setJmrFilter({
                  ...jmrFilter,
                  jobType: JmrJobTypes.ProcessPath,
                }),
              );
              closeModal();
            }}
          >
            Go back to Process Path
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default TldAlertModal;
